import {
  bodyVariableCount,
  TEMPLATE_LANGUAGES,
  TEMPLATE_LIMITS,
  type TemplateDraftInput,
  type TemplateLanguage,
  templateDraftProblems,
  templateNameFrom,
} from "@orbis/shared";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { type FormEvent, useEffect, useRef, useState } from "react";
import { useToast } from "../../components/ui";
import { ApiError, api } from "../../lib/api";

type Button = TemplateDraftInput["buttons"][number];

const CATEGORY_HELP = {
  UTILITY: "Avisos de algo que el cliente ya pidió: su pedido, su cita, su caso, su cotización.",
  MARKETING: "Promociones, novedades o invitaciones. Meta las cobra más caras y revisa más.",
} as const;

/** The body with each {{n}} replaced by its example, as the customer would read it. */
function filled(text: string, examples: string[]) {
  return text.replace(/\{\{\s*(\d+)\s*\}\}/g, (_, n: string) => examples[Number(n) - 1] || `{{${n}}}`);
}

/**
 * A new WhatsApp template (f6-08): Orbis checks Meta's rules as you write, shows how it
 * will look, and sends it for review. Meta approves or rejects it, usually in minutes.
 */
export function TemplateEditorDialog({ onClose }: { onClose: () => void }) {
  const dialog = useRef<HTMLDialogElement>(null);
  const body = useRef<HTMLTextAreaElement>(null);
  const qc = useQueryClient();
  const toast = useToast();
  const [label, setLabel] = useState("");
  const [draft, setDraft] = useState<TemplateDraftInput>({
    name: "",
    language: "es",
    category: "UTILITY",
    headerText: "",
    headerExample: "",
    bodyText: "",
    bodyExamples: [],
    footerText: "",
    buttons: [],
  });
  const [touched, setTouched] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const accounts = useQuery({
    queryKey: ["templates", "accounts"],
    queryFn: () => api.get<{ items: { wabaId: string; numbers: string[] }[] }>("/templates/accounts"),
  });
  useEffect(() => {
    dialog.current?.showModal();
  }, []);

  const set = (patch: Partial<TemplateDraftInput>) => setDraft((d) => ({ ...d, ...patch }));
  const count = bodyVariableCount(draft.bodyText);
  const examples = Array.from({ length: count }, (_, i) => draft.bodyExamples[i] ?? "");
  const headerHasVar = /\{\{\s*1\s*\}\}/.test(draft.headerText ?? "");
  const problems = templateDraftProblems({ ...draft, bodyExamples: examples });

  function addVariable() {
    const el = body.current;
    const token = `{{${count + 1}}}`;
    const at = el?.selectionStart ?? draft.bodyText.length;
    set({ bodyText: draft.bodyText.slice(0, at) + token + draft.bodyText.slice(at) });
    requestAnimationFrame(() => el?.focus());
  }
  const setButton = (i: number, patch: Partial<Button>) =>
    set({ buttons: draft.buttons.map((b, j) => (j === i ? ({ ...b, ...patch } as Button) : b)) });

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setTouched(true);
    if (problems.length) return;
    setError(null);
    setBusy(true);
    try {
      await api.post("/templates", {
        ...draft,
        bodyExamples: examples,
        headerExample: headerHasVar ? draft.headerExample : null,
        wabaId: draft.wabaId || undefined,
      });
      qc.invalidateQueries({ queryKey: ["templates"] });
      toast("Plantilla enviada a Meta. Suele aprobarse en unos minutos.");
      dialog.current?.close();
    } catch (err) {
      const d = err instanceof ApiError ? err.details : undefined;
      setError(
        d?.reason === "meta_refused"
          ? `Meta no la aceptó: «${String(d.meta)}»`
          : d?.reason === "invalid_template" && Array.isArray(d.problems)
            ? String(d.problems[0])
            : d?.reason === "no_number"
              ? "La empresa todavía no tiene un número de WhatsApp conectado."
              : "No se pudo enviar. Intenta de nuevo.",
      );
    } finally {
      setBusy(false);
    }
  }

  const many = (accounts.data?.items.length ?? 0) > 1;
  return (
    <dialog ref={dialog} onClose={onClose} aria-labelledby="tpl-title" className="template-editor">
      <form className="dialog-form" onSubmit={onSubmit}>
        <h3 id="tpl-title">Nueva plantilla</h3>
        <div className="template-editor-grid">
          <div className="template-editor-fields">
            <label className="field">
              <span>Nombre</span>
              <input
                className="input"
                required
                placeholder="Ej.: Recordatorio de cita"
                value={label}
                onChange={(e) => {
                  setLabel(e.target.value);
                  set({ name: templateNameFrom(e.target.value) });
                }}
              />
              {draft.name && <small className="faint mono">{draft.name}</small>}
            </label>
            <div className="template-editor-row">
              <label className="field">
                <span>Tipo</span>
                <select
                  className="select"
                  value={draft.category}
                  onChange={(e) => set({ category: e.target.value as TemplateDraftInput["category"] })}
                >
                  <option value="UTILITY">Utilidad</option>
                  <option value="MARKETING">Marketing</option>
                </select>
              </label>
              <label className="field">
                <span>Idioma</span>
                <select
                  className="select"
                  value={draft.language}
                  onChange={(e) => set({ language: e.target.value as TemplateLanguage })}
                >
                  {Object.entries(TEMPLATE_LANGUAGES).map(([k, v]) => (
                    <option key={k} value={k}>
                      {v}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <p className="faint">{CATEGORY_HELP[draft.category]}</p>
            {many && (
              <label className="field">
                <span>Cuenta de WhatsApp</span>
                <select
                  className="select"
                  required
                  value={draft.wabaId ?? ""}
                  onChange={(e) => set({ wabaId: e.target.value || undefined })}
                >
                  <option value="" disabled>
                    Elige el número
                  </option>
                  {accounts.data?.items.map((a) => (
                    <option key={a.wabaId} value={a.wabaId}>
                      {a.numbers.join(", ")}
                    </option>
                  ))}
                </select>
              </label>
            )}
            <label className="field">
              <span>Encabezado (opcional)</span>
              <input
                className="input"
                maxLength={TEMPLATE_LIMITS.header}
                placeholder="Ej.: Tu cita en la tienda"
                value={draft.headerText ?? ""}
                onChange={(e) => set({ headerText: e.target.value })}
              />
            </label>
            {headerHasVar && (
              <label className="field">
                <span>Ejemplo para {"{{1}}"} del encabezado</span>
                <input
                  className="input"
                  placeholder="Ej.: Ana"
                  value={draft.headerExample ?? ""}
                  onChange={(e) => set({ headerExample: e.target.value })}
                />
              </label>
            )}
            <label className="field">
              <span>
                Mensaje{" "}
                <small className="faint">
                  {draft.bodyText.length}/{TEMPLATE_LIMITS.body}
                </small>
              </span>
              <textarea
                ref={body}
                className="input"
                rows={5}
                required
                maxLength={TEMPLATE_LIMITS.body}
                placeholder="Ej.: Hola {{1}}, te recordamos tu cita del {{2}}. Si no puedes venir, respóndenos este mensaje."
                value={draft.bodyText}
                onChange={(e) => set({ bodyText: e.target.value })}
              />
            </label>
            <div>
              <button className="btn btn-sm" type="button" onClick={addVariable}>
                + Variable
              </button>
              <small className="faint"> Una parte que cambia en cada envío, como el nombre del cliente.</small>
            </div>
            {count > 0 && (
              <div className="template-editor-examples">
                {examples.map((v, i) => (
                  // biome-ignore lint/suspicious/noArrayIndexKey: each example is variable {{i + 1}}
                  <label key={i} className="field">
                    <span>Ejemplo para {`{{${i + 1}}}`}</span>
                    <input
                      className="input"
                      placeholder={i === 0 ? "Ej.: Ana" : "Ej.: lunes 3 a las 10 a. m."}
                      value={v}
                      onChange={(e) => {
                        const next = [...examples];
                        next[i] = e.target.value;
                        set({ bodyExamples: next });
                      }}
                    />
                  </label>
                ))}
              </div>
            )}
            <label className="field">
              <span>Pie (opcional)</span>
              <input
                className="input"
                maxLength={TEMPLATE_LIMITS.footer}
                placeholder="Ej.: Responde BAJA si no quieres más avisos"
                value={draft.footerText ?? ""}
                onChange={(e) => set({ footerText: e.target.value })}
              />
            </label>
            <div className="template-editor-buttons">
              <span className="field-label">Botones (opcional)</span>
              {draft.buttons.map((b, i) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: buttons are edited in place by position
                <div key={i} className="template-editor-button">
                  <input
                    className="input"
                    maxLength={TEMPLATE_LIMITS.buttonText}
                    placeholder={
                      b.type === "QUICK_REPLY"
                        ? "Ej.: Confirmo"
                        : b.type === "URL"
                          ? "Ej.: Ver ubicación"
                          : "Ej.: Llamar"
                    }
                    aria-label="Texto del botón"
                    value={b.text}
                    onChange={(e) => setButton(i, { text: e.target.value })}
                  />
                  {b.type === "URL" && (
                    <input
                      className="input"
                      placeholder="https://tu-sitio.com/ubicacion"
                      aria-label="Enlace"
                      value={b.url}
                      onChange={(e) => setButton(i, { url: e.target.value })}
                    />
                  )}
                  {b.type === "PHONE_NUMBER" && (
                    <input
                      className="input"
                      placeholder="+506 2222 3333"
                      aria-label="Teléfono"
                      value={b.phone}
                      onChange={(e) => setButton(i, { phone: e.target.value })}
                    />
                  )}
                  <button
                    className="btn btn-sm btn-ghost"
                    type="button"
                    aria-label="Quitar botón"
                    onClick={() => set({ buttons: draft.buttons.filter((_, j) => j !== i) })}
                  >
                    Quitar
                  </button>
                </div>
              ))}
              {draft.buttons.length < TEMPLATE_LIMITS.buttons && (
                <div className="row">
                  <button
                    className="btn btn-sm"
                    type="button"
                    onClick={() => set({ buttons: [...draft.buttons, { type: "QUICK_REPLY", text: "" }] })}
                  >
                    + Respuesta rápida
                  </button>
                  <button
                    className="btn btn-sm"
                    type="button"
                    disabled={draft.buttons.filter((b) => b.type === "URL").length >= TEMPLATE_LIMITS.urls}
                    onClick={() => set({ buttons: [...draft.buttons, { type: "URL", text: "", url: "" }] })}
                  >
                    + Enlace
                  </button>
                  <button
                    className="btn btn-sm"
                    type="button"
                    disabled={draft.buttons.some((b) => b.type === "PHONE_NUMBER")}
                    onClick={() => set({ buttons: [...draft.buttons, { type: "PHONE_NUMBER", text: "", phone: "" }] })}
                  >
                    + Llamar
                  </button>
                </div>
              )}
            </div>
          </div>
          <aside className="template-preview" aria-label="Así lo verá el cliente">
            <span className="faint">Así lo verá el cliente</span>
            <div className="template-bubble">
              {draft.headerText && (
                <strong>{draft.headerText.replace(/\{\{\s*1\s*\}\}/, draft.headerExample || "{{1}}")}</strong>
              )}
              <p>{filled(draft.bodyText, examples) || "Tu mensaje aparece aquí."}</p>
              {draft.footerText && <small>{draft.footerText}</small>}
            </div>
            {draft.buttons.map((b, i) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: preview follows the editor's order
              <div key={i} className="template-bubble-button">
                {b.type === "URL" ? "↗ " : b.type === "PHONE_NUMBER" ? "✆ " : ""}
                {b.text || "Botón"}
              </div>
            ))}
          </aside>
        </div>
        {touched && problems.length > 0 && (
          <ul className="banner banner-warn template-problems">
            {problems.map((p) => (
              <li key={p}>{p}</li>
            ))}
          </ul>
        )}
        {error && <div className="banner banner-warn">{error}</div>}
        <div className="row">
          <button className="btn" type="button" onClick={() => dialog.current?.close()}>
            Cancelar
          </button>
          <button className="btn btn-primary" type="submit" disabled={busy}>
            {busy ? "Enviando…" : "Enviar a Meta para revisión"}
          </button>
        </div>
      </form>
    </dialog>
  );
}
